import { Axios } from "api/axios";

const DEFAULT_STALE_MS = 60_000;
const MAX_ENTRIES = 100;

type CacheEntry<T> = {
  expiresAt: number;
  value: T;
};

const cache = new Map<string, CacheEntry<unknown>>();
const inFlight = new Map<string, Promise<unknown>>();

const stableParamsKey = (params?: Record<string, string>) => {
  if (!params) return "";
  return Object.keys(params)
    .sort()
    .map((key) => `${key}=${params[key]}`)
    .join("&");
};

const prune = () => {
  const now = Date.now();
  cache.forEach((entry, key) => {
    if (entry.expiresAt <= now) cache.delete(key);
  });
  while (cache.size > MAX_ENTRIES) {
    const oldest = cache.keys().next().value;
    if (!oldest) return;
    cache.delete(oldest);
  }
};

export const cachedDashboardGet = async <T>(
  url: string,
  params?: Record<string, string>,
  staleMs = DEFAULT_STALE_MS,
): Promise<T> => {
  prune();
  const key = `${url}?${stableParamsKey(params)}`;
  const cached = cache.get(key) as CacheEntry<T> | undefined;
  if (cached && cached.expiresAt > Date.now()) return cached.value;

  const existing = inFlight.get(key) as Promise<T> | undefined;
  if (existing) return existing;

  const request = Axios.get<T>(url, { params })
    .then((response) => {
      cache.set(key, {
        value: response.data,
        expiresAt: Date.now() + staleMs,
      });
      return response.data;
    })
    .finally(() => {
      inFlight.delete(key);
    });

  inFlight.set(key, request);
  return request;
};

export const clearDashboardRequestCache = () => {
  cache.clear();
  inFlight.clear();
};
